import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import Logger from '@renanmalato/logger-plus'
import { nextPaint } from '@/lib/utils/ui/nextPaint'
import userProfileService, { type GridDensity, type GridSortClick, type UserPreferences } from '@/lib/services/UserProfileService'

// ------------------------------------------------- //
//                                                   //
//                                                   //
//  User Preferences                                 //
//                                                   //
//                                                   //
// ------------------------------------------------- //

/*

································································
·                                                              ·
· Backed by crm-users.preferences, mirrored to localStorage.   ·
· localStorage paints at boot; the server value arrives        ·
· before app.mount() and wins.                                 ·
·                                                              ·
· Holds the FULL preferences object — a partial payload        ·
· drops every area it omits.                                   ·
·                                                              ·
································································

*/

const STORAGE_KEY = 'user_preferences'
const SYNC_DEBOUNCE_MS = 700

const FONT_BOOST_MIN = -2
const FONT_BOOST_MAX = 4


export const useUserPreferencesStore = defineStore('userPreferences', () => {

  const preferences = ref<UserPreferences>({})
  const hydrated = ref(false)
  const syncFailed = ref(false)
  const isApplying = ref(false)

  // ------------------------------- //
  //  Derived                        //
  // ------------------------------- //

  const density = computed<GridDensity>(() => preferences.value.visual?.grid?.density ?? 'DEFAULT')
  const fontBoost = computed<number>(() => preferences.value.visual?.typography?.fontBoost ?? 0)
  const sortClick = computed<GridSortClick>(() => preferences.value.visual?.grid?.sortClick ?? 'SINGLE')
  const toolbarBottomLeft = computed<string | undefined>(() => preferences.value.toolbar?.findProperties?.bottomLeft)

  // ------------------------------- //
  //  Apply                          //
  // ------------------------------- //

  const applyToDom = () => {
    const root = document.documentElement
    root.dataset.gridDensity = density.value
    root.style.setProperty('--font-boost', `${fontBoost.value}px`)
  }

  // ------------------------------- //
  //  Local Mirror                   //
  // ------------------------------- //

  const persistLocal = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences.value))
    } catch (error) {
      Logger.warn('⚠️ Could not mirror preferences to localStorage', 'useUserPreferencesStore', { error })
    }
  }

  const readLocal = (): UserPreferences | null => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return null

      const parsed = JSON.parse(raw)

      /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */
      /*  Legacy Device-Only Shape  */
      /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */

      if (typeof parsed?.tightRows === 'boolean' && !parsed.visual) {
        return { visual: { grid: { density: parsed.tightRows ? 'TIGHT' : 'DEFAULT' } } }
      }

      return parsed as UserPreferences
    } catch {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
  }

  // ------------------------------- //
  //  Server Sync                    //
  // ------------------------------- //

  let syncSeq = 0

  const pushToServer = async () => {
    const seq = ++syncSeq

    try {
      const persisted = await userProfileService.updateCurrentUserPreferences(
        JSON.parse(JSON.stringify(preferences.value))
      )

      /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */
      /*  Drop Superseded Response  */
      /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */

      if (seq !== syncSeq) return

      preferences.value = persisted ?? preferences.value
      syncFailed.value = false
      persistLocal()
    } catch (error) {
      syncFailed.value = true
      Logger.error('❌ Failed to sync user preferences', 'useUserPreferencesStore', { error })
    }
  }

  // ------------------------------- //
  //  Queue Sync                     //
  // ------------------------------- //

  const queueSync = useDebounceFn(pushToServer, SYNC_DEBOUNCE_MS)

  /* ·····························································
  ·                                                              ·
  ·  Swapping the density attribute invalidates custom           ·
  ·  properties document-wide, so the restyle would batch into   ·
  ·  the same frame as the control that triggered it and the     ·
  ·  click would look dead for seconds. Paint the optimistic     ·
  ·  state first, then do the heavy work.                        ·
  ·                                                              ·
  ····························································· */

  const commit = async () => {
    isApplying.value = true
    await nextPaint()

    applyToDom()
    await nextPaint()

    isApplying.value = false
    persistLocal()
    if (hydrated.value) queueSync()
  }

  // ------------------------------- //
  //  Setters                        //
  // ------------------------------- //

  const setDensity = (value: GridDensity) => {
    preferences.value = {
      ...preferences.value,
      visual: {
        ...preferences.value.visual,
        grid: { ...preferences.value.visual?.grid, density: value }
      }
    }
    commit()
  }

  const setSortClick = (value: GridSortClick) => {
    preferences.value = {
      ...preferences.value,
      visual: {
        ...preferences.value.visual,
        grid: { ...preferences.value.visual?.grid, sortClick: value }
      }
    }
    commit()
  }

  const setFontBoost = (value: number) => {
    const clamped = Math.min(FONT_BOOST_MAX, Math.max(FONT_BOOST_MIN, Math.round(value)))

    preferences.value = {
      ...preferences.value,
      visual: {
        ...preferences.value.visual,
        typography: { ...preferences.value.visual?.typography, fontBoost: clamped }
      }
    }
    commit()
  }

  const setToolbarBottomLeft = (value: string) => {
    preferences.value = {
      ...preferences.value,
      toolbar: {
        ...preferences.value.toolbar,
        findProperties: { ...preferences.value.toolbar?.findProperties, bottomLeft: value }
      }
    }
    commit()
  }

  // ------------------------------- //
  //  Boot                           //
  // ------------------------------- //

  const init = () => {
    const saved = readLocal()
    if (saved) preferences.value = saved
    applyToDom()
  }

  /* ·····························································
  ·                                                              ·
  ·  The list can be opened in its own window, and the density    ·
  ·  attribute lives on that document's own <html>. storage       ·
  ·  events fire only in the OTHER tabs, which is exactly the     ·
  ·  cross-document channel needed — no control to animate here,  ·
  ·  so it applies straight away.                                 ·
  ·                                                              ·
  ····························································· */

  window.addEventListener('storage', async (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return

    let incoming: UserPreferences
    try {
      incoming = JSON.parse(event.newValue) as UserPreferences
    } catch {
      Logger.warn('⚠️ Ignored malformed preferences from another tab', 'useUserPreferencesStore')
      return
    }

    preferences.value = incoming
    isApplying.value = true
    applyToDom()

    await nextPaint()
    isApplying.value = false
  })

  const hydrateFromServer = (serverPreferences: UserPreferences | null | undefined) => {
    const server = serverPreferences ?? {}
    const localVisual = preferences.value.visual

    /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */
    /*  First-Run Adoption        */
    /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */

    const adoptLocal = !server.visual && !!localVisual

    preferences.value = adoptLocal ? { ...server, visual: localVisual } : server
    hydrated.value = true
    applyToDom()
    persistLocal()

    if (adoptLocal) queueSync()
  }

  const markHydrationFailed = () => {
    hydrated.value = true
  }

  return {
    preferences,
    hydrated,
    syncFailed,
    isApplying,
    density,
    fontBoost,
    sortClick,
    toolbarBottomLeft,
    setDensity,
    setFontBoost,
    setSortClick,
    setToolbarBottomLeft,
    init,
    hydrateFromServer,
    markHydrationFailed
  }
})
